import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { api, type AppInfo } from "./api";

// The hub catalog, read through boo-backend (the browser holds no hub token).

// Paginated, searchable catalog. A new query/category/connectedBy starts
// over; `loadMore()` appends the next page. Debounce the query upstream if
// wiring to a text input.
export function useCatalog(
  options: {
    query?: string;
    category?: string;
    connectedBy?: "hub" | "app";
    pageSize?: number;
  } = {},
) {
  const { query, category, connectedBy, pageSize } = options;
  const result = useInfiniteQuery({
    queryKey: ["hub-catalog", query ?? "", category ?? "", connectedBy ?? "", pageSize ?? 0],
    queryFn: ({ pageParam }) =>
      api.integrations.apps({
        q: query || undefined,
        category: category || undefined,
        connectedBy,
        cursor: pageParam || undefined,
        limit: pageSize,
      }),
    initialPageParam: "",
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    staleTime: 5 * 60 * 1000,
  });
  const apps: AppInfo[] = result.data?.pages.flatMap((p) => p.apps) ?? [];
  return {
    apps,
    loadMore: () => {
      if (result.hasNextPage && !result.isFetchingNextPage) void result.fetchNextPage();
    },
    hasMore: result.hasNextPage,
    loading: result.isPending || result.isFetchingNextPage,
    error: result.error ? result.error.message : null,
  };
}

// Category taxonomy for filter UIs. Static per hub deployment, so it is kept
// for the session.
export function useCategories() {
  const result = useQuery({
    queryKey: ["hub-categories"],
    queryFn: api.integrations.categories,
    staleTime: Infinity,
    // Categories are an enhancement — a failure means "no filters".
    retry: false,
  });
  return { categories: result.data ?? [], loading: result.isPending && !result.isError };
}
